import React, { useEffect, useState } from 'react';
import { fetchProjekty } from '../lib/projectsApi';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import type { ProjectCard } from '../types';

const Projects: React.FC = () => {
  const conferencePostUrl =
    'https://www.linkedin.com/posts/michal-ch_sustainabilitydialogue-futureplans-inspiration-activity-7208800624572780544-pbGS?utm_source=share&utm_medium=member_desktop&rcm=ACoAAC9evRUBw4TJeaiRZwojzNVHQZyVqG7txZo';
  const [projects, setProjects] = useState<ProjectCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isSupabaseConfigured()) {
        setError(
          'Supabase není nakonfigurován. Zkopíruj .env.example na .env.local a doplň REACT_APP_SUPABASE_URL a REACT_APP_SUPABASE_PUBLISHABLE_KEY.'
        );
        setLoading(false);
        return;
      }

      try {
        const data = await fetchProjekty();
        if (!cancelled) {
          setProjects(data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Nepodařilo se načíst projekty.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const shorten = (value: string, max = 120): string => {
    if (value.length <= max) {
      return value;
    }
    return `${value.slice(0, max).trimEnd()}...`;
  };

  return (
    <section id="projects" className="py-12 md:py-20">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-4xl md:text-6xl font-bold mb-8 md:mb-16">PROJECTS</h2>

        {loading && (
          <p className="brutalist-text text-sm md:text-base" aria-live="polite">
            Loading projects…
          </p>
        )}

        {error && !loading && (
          <p className="brutalist-text text-sm md:text-base text-red-300 border-2 border-red-500 p-4 bg-red-950/40" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && projects.length === 0 && (
          <p className="brutalist-text text-sm md:text-base">No projects to display.</p>
        )}

        {!loading && !error && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
          {projects.map((project) => {
            const isConferenceProject = /conference|konference/i.test(project.title);
            const projectHref = isConferenceProject ? conferencePostUrl : project.links.demo;

            return (
            <a
              key={project.id}
              href={projectHref ?? '#projects'}
              target={projectHref ? '_blank' : undefined}
              rel={projectHref ? 'noopener noreferrer' : undefined}
              className={`brutalist-container block transform hover:rotate-1 transition-transform ${
                projectHref ? 'cursor-pointer' : 'pointer-events-none'
              }`}
              aria-label={projectHref ? `Open ${project.title}` : undefined}
            >
              <div className="aspect-video bg-surface-alt mb-4 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{project.title}</h3>
              <p className="brutalist-text text-sm md:text-base">
                {shorten(project.description)}
              </p>
            </a>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
